/**
 * Stripe subscription status → display.
 *
 * Seven statuses collapse to four things a shop needs to understand. The ACH
 * ones matter most: `incomplete` is the NORMAL state for days after a
 * successful Checkout, because ACH settles over ~3-5 business days. Showing a
 * raw enum there reads as a failure at the moment the user has just paid.
 *
 * ZERO IMPORTS BY DESIGN — unit-tested directly by
 * `scripts/test/subscriptionStatus.test.mjs`. Do not add imports.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * 'YYYY-MM-DD...' -> '12 Sep 2026'.
 *
 * Parsed by hand, never through Date: `new Date('2026-09-12')` is UTC midnight
 * and renders as 11 Sep in HST/PST. See DATA_CONVENTIONS §3.2.
 */
export function formatPeriodDate(value) {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return '';
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return '';
  return `${Number(m[3])} ${month} ${m[1]}`;
}

function statusOf(sub) {
  return String((sub && sub.subscription_status) || '').trim().toLowerCase();
}

/**
 * A live subscription. `canceled` and absent both read false — both offer
 * Subscribe. `incomplete_expired` is also false: it is TERMINAL in Stripe
 * (the first payment never succeeded), so it must offer Subscribe again
 * rather than reading as an ongoing subscription.
 */
export function hasSubscription(sub) {
  const s = statusOf(sub);
  return s !== '' && s !== 'canceled' && s !== 'incomplete_expired';
}

/** Awaiting bank verification / ACH settlement. `incomplete_expired` is terminal, not pending. */
export function isPending(sub) {
  const s = statusOf(sub);
  return s === 'incomplete';
}

/** A charge has failed. `unpaid` is the terminal end of the same story. */
export function isPastDue(sub) {
  const s = statusOf(sub);
  return s === 'past_due' || s === 'unpaid';
}

export function primaryAction(sub) {
  return hasSubscription(sub) ? 'manage' : 'subscribe';
}

export function statusLabel(sub) {
  const s = statusOf(sub);
  const periodEnd = formatPeriodDate(sub && sub.current_period_end);
  switch (s) {
    case '':
      return 'No subscription';
    case 'trialing':
      return periodEnd ? `Free trial · first charge ${periodEnd}` : 'Free trial';
    case 'active':
      return periodEnd ? `Active · renews ${periodEnd}` : 'Active';
    case 'incomplete':
      return 'Bank verification in progress';
    case 'incomplete_expired':
      return 'Signup did not complete';
    case 'past_due':
    case 'unpaid': {
      const since = formatPeriodDate(sub && sub.past_due_since);
      return since ? `Payment failed · since ${since}` : 'Payment failed';
    }
    case 'canceled':
      return 'Canceled';
    default:
      // Stripe may add statuses. Showing the raw value beats showing nothing.
      return s;
  }
}

export function statusVariant(sub) {
  const s = statusOf(sub);
  if (s === 'active') return 'success';
  if (s === 'trialing') return 'info';
  if (isPending(sub)) return 'warning';
  if (isPastDue(sub)) return 'danger';
  // incomplete_expired falls through to here — terminal, like canceled.
  return 'secondary';
}
